import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { TrendingUp, TrendingDown, DollarSign, BarChart3, Calendar, AlertCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Pricing = () => {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${API}/pricing/current`);
        if (response.data.success) {
          setPricing(response.data);
        }
      } catch (err) {
        console.error('Failed to load pricing:', err);
        setError('Unable to load latest pricing. Please refresh.');
      } finally {
        setLoading(false);
      }
    };
    fetchPricing();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-emerald-600 mx-auto mb-3" />
          <p className="text-gray-600">Loading latest pricing...</p>
        </div>
      </div>
    );
  }

  if (error || !pricing) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <AlertCircle className="h-10 w-10 text-amber-600 mx-auto mb-3" />
          <p className="text-gray-700">{error || 'Pricing unavailable.'}</p>
        </div>
      </div>
    );
  }

  const { lastUpdated, southAfrica, international, trends, internationalSource, saSource, saAsOf } = pricing;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50 pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="mb-4 bg-emerald-600 text-white text-base px-4 py-1">Live Market Pricing</Badge>
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Fuel Pricing Dashboard</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
            Stay informed with the latest fuel prices for South Africa and international markets
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
            <Calendar className="h-4 w-4" />
            <span>Last Updated: {lastUpdated}</span>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-6 px-6 bg-amber-50 border-y border-amber-200">
        <div className="max-w-7xl mx-auto flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-900">
            <strong>Disclaimer:</strong> Prices shown are indicative and updated daily based on Department of Energy adjustments and international benchmarks.
            For accurate wholesale quotes, please <a href="/contact" className="underline font-semibold">request a quote</a> from our sales team.
          </p>
        </div>
      </section>

      {/* South African Prices */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <Badge className="bg-teal-100 text-teal-700 text-base px-4 py-1">South Africa</Badge>
              {saSource === "live" && (
                <Badge className="bg-red-100 text-red-700 text-sm px-3 py-1">
                  <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-2 animate-pulse"></span>
                  LIVE
                </Badge>
              )}
              {saAsOf && (
                <span className="text-sm text-gray-500">Official: {saAsOf}</span>
              )}
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">SA Fuel Prices</h2>
            <p className="text-xl text-gray-600">
              {saSource === "live"
                ? "Official wholesale and retail prices from the Fuels Industry Association of South Africa"
                : "Wholesale and retail fuel prices in South African Rand"}
            </p>
          </div>

          <Card className="border-2 border-emerald-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-emerald-600" />
                South African Petroleum Prices
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead>Region</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead className="text-right">Change</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {southAfrica.map((item, index) => (
                      <TableRow key={index} className="hover:bg-emerald-50 transition-colors">
                        <TableCell className="font-medium">{item.product}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="border-teal-300 text-teal-700">
                            {item.region}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-bold text-gray-900">
                          R{item.price.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-gray-600">{item.unit}</TableCell>
                        <TableCell className="text-right">
                          <div className={`inline-flex items-center gap-1 ${item.change > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                            {item.change > 0 ? (
                              <TrendingUp className="h-4 w-4" />
                            ) : (
                              <TrendingDown className="h-4 w-4" />
                            )}
                            <span className="font-semibold">
                              {item.change > 0 ? '+' : ''}{item.change.toFixed(2)}
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* International Prices */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <Badge className="bg-emerald-100 text-emerald-700 text-base px-4 py-1">International Markets</Badge>
              {internationalSource === "live" && (
                <Badge className="bg-red-100 text-red-700 text-sm px-3 py-1">
                  <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-2 animate-pulse"></span>
                  LIVE
                </Badge>
              )}
              {internationalSource === "partial" && (
                <Badge className="bg-amber-100 text-amber-700 text-sm px-3 py-1">PARTIALLY LIVE</Badge>
              )}
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Global Oil & Gas Prices</h2>
            <p className="text-xl text-gray-600">
              International benchmark prices in USD
              {internationalSource === "live" && " — live data updates every hour"}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {international.map((item, index) => (
              <Card key={index} className="border-2 border-gray-200 hover:border-emerald-300 hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg mb-1">{item.product}</CardTitle>
                      <CardDescription>{item.unit}</CardDescription>
                    </div>
                    <div className={`p-2 rounded-lg ${item.change > 0 ? 'bg-red-50' : 'bg-emerald-50'}`}>
                      {item.change > 0 ? (
                        <TrendingUp className="h-5 w-5 text-red-600" />
                      ) : (
                        <TrendingDown className="h-5 w-5 text-emerald-600" />
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-900 mb-2">
                    ${item.price.toFixed(2)}
                  </div>
                  <div className={`flex items-center gap-1 text-sm ${item.change > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    <span className="font-semibold">
                      {item.change > 0 ? '+' : ''}{item.change.toFixed(2)}
                    </span>
                    <span>vs last month</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Price Trends */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <Badge className="mb-4 bg-teal-100 text-teal-700">6-Month Trends</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Price Movement</h2>
            <p className="text-xl text-gray-600">
              Historical price trends over the last 6 months
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <PriceTrendCard title="Diesel 50ppm (SA)" data={trends.diesel50ppm} unit="R/L" color="emerald" />
            <PriceTrendCard title="Petrol ULP95 (SA)" data={trends.petrol95} unit="R/L" color="teal" />
            <PriceTrendCard title="Brent Crude Oil" data={trends.brentCrude} unit="USD/bbl" color="cyan" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-gradient-to-br from-emerald-600 to-teal-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <BarChart3 className="h-16 w-16 mx-auto mb-6 opacity-90" />
          <h2 className="text-4xl font-bold mb-6">Get Competitive Wholesale Pricing</h2>
          <p className="text-xl mb-8 text-emerald-50">
            Request a customized quote based on your volume and delivery requirements
          </p>
          <a href="/contact" className="inline-block">
            <button className="bg-white text-emerald-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-emerald-50 transition-all duration-300 hover:shadow-xl hover:scale-105">
              Request a Quote
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

const PriceTrendCard = ({ title, data, unit, color }) => {
  const maxPrice = Math.max(...data.map(d => d.price));
  const minPrice = Math.min(...data.map(d => d.price));
  const range = maxPrice - minPrice;
  const currentPrice = data[data.length - 1].price;
  const previousPrice = data[data.length - 2].price;
  const change = currentPrice - previousPrice;
  const changePercent = ((change / previousPrice) * 100).toFixed(2);

  const colorClasses = {
    emerald: { bg: 'bg-emerald-500', border: 'border-emerald-200' },
    teal: { bg: 'bg-teal-500', border: 'border-teal-200' },
    cyan: { bg: 'bg-cyan-500', border: 'border-cyan-200' }
  };
  const colors = colorClasses[color];

  return (
    <Card className={`border-2 ${colors.border} hover:shadow-xl transition-all duration-300`}>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
        <CardDescription>{unit}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          <div className="text-3xl font-bold text-gray-900">
            {unit.includes('USD') ? '$' : 'R'}{currentPrice.toFixed(2)}
          </div>
          <div className={`flex items-center gap-1 text-sm ${change > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
            {change > 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
            <span className="font-semibold">{change > 0 ? '+' : ''}{change.toFixed(2)} ({changePercent}%)</span>
          </div>
        </div>

        <div className="flex items-end justify-between h-32 gap-2">
          {data.map((item, index) => {
            const height = range > 0 ? ((item.price - minPrice) / range) * 100 : 50;
            return (
              <div key={index} className="flex flex-col items-center gap-1 flex-1">
                <div
                  className={`w-full ${colors.bg} rounded-t transition-all duration-500 hover:opacity-80`}
                  style={{ height: `${Math.max(height, 10)}%` }}
                  title={`${item.month}: ${item.price.toFixed(2)}`}
                ></div>
                <span className="text-xs text-gray-600">{item.month}</span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default Pricing;
